<template>
  <div v-if="tabs.length > 0" ref="window" class="mav-stream-window" :style="windowStyle">
    <!-- Title bar -->
    <div class="mav-title-bar" @mousedown="startDrag">
      <div class="mav-title-icon">
        <v-icon small color="white">mdi-robot</v-icon>
      </div>
      <span class="mav-title-text">MAV Prediction Stream</span>
      <v-spacer />
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            v-if="isDebugEnabled()"
            icon
            x-small
            dark
            v-bind="attrs"
            v-on="on"
            @click="debugMode = !debugMode"
          >
            <v-icon small :color="debugMode ? '#FFC107' : 'rgba(255,255,255,0.4)'">mdi-bug</v-icon>
          </v-btn>
        </template>
        <span>{{ debugMode ? 'Debug mode ON' : 'Debug mode OFF' }}</span>
      </v-tooltip>
      <v-btn icon x-small dark @click="minimized = !minimized">
        <v-icon small color="#78909C">{{
          minimized ? 'mdi-window-maximize' : 'mdi-window-minimize'
        }}</v-icon>
      </v-btn>
      <v-btn icon x-small dark @click="closeAll">
        <v-icon small color="#78909C">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Debug mode banner -->
    <div v-if="debugMode && !minimized" class="mav-debug-banner">
      <v-icon x-small color="#FFC107" class="mr-1">mdi-wrench</v-icon>
      Debug Mode — iteration-level events, full raw tool output
    </div>

    <!-- Tabs -->
    <div v-if="!minimized" class="mav-tab-bar">
      <div
        v-for="(tab, index) in tabs"
        :key="tab.id"
        class="mav-tab-item"
        :class="{
          'mav-tab-item--active': activeTab === index,
          'mav-tab-item--drag-over': dragOverIndex === index,
          'mav-tab-item--paused': tab.paused,
        }"
        draggable="true"
        @dragstart="onTabDragStart(index, $event)"
        @dragover.prevent="onTabDragOver(index)"
        @dragleave="onTabDragLeave"
        @drop.prevent="onTabDrop(index)"
        @dragend="onTabDragEnd"
        @click="activeTab = index"
      >
        <v-icon x-small class="mav-tab-drag-grip mr-1">mdi-drag-vertical</v-icon>
        <span class="text-truncate" style="max-width: 110px">{{ tab.domain }}</span>
        <v-progress-circular
          v-if="tab.streaming"
          indeterminate
          size="12"
          width="2"
          color="#7B1FA2"
          class="ml-2"
        />
        <v-icon v-else-if="tab.paused" x-small color="#FF9800" class="ml-1"
          >mdi-hand-back-right</v-icon
        >
        <v-icon v-else-if="tab.error" x-small color="#F44336" class="ml-1">mdi-alert-circle</v-icon>
        <v-icon v-else x-small color="#4CAF50" class="ml-1">mdi-check-circle</v-icon>
        <v-btn icon x-small class="ml-1 mav-tab-close" @click.stop="closeTab(index)">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Tab content -->
    <div v-if="!minimized" class="mav-content">
      <div
        v-for="(tab, index) in tabs"
        v-show="activeTab === index"
        :key="tab.id"
        :ref="'stream-' + tab.id"
        class="mav-stream-output"
      >
        <div
          v-for="(event, i) in tab.events"
          :key="i"
          class="mav-event"
          :class="'mav-event--' + event.type"
        >
          <!-- Thought -->
          <template v-if="event.type === 'thought'">
            <div class="mav-event-header">
              <v-icon x-small color="#CE93D8">mdi-head-lightbulb</v-icon>
              <span class="mav-event-label">Thinking</span>
              <span class="mav-event-iter">Step {{ event.iteration }}</span>
            </div>
            <div class="mav-event-body">{{ event.thought }}</div>
          </template>

          <!-- Tool call -->
          <template v-if="event.type === 'tool_call'">
            <div class="mav-event-header">
              <v-icon x-small color="#64B5F6">mdi-wrench</v-icon>
              <span class="mav-event-label">{{ event.tool_name }}</span>
              <span class="mav-event-iter">Step {{ event.iteration }}</span>
            </div>
            <div v-if="event.thought" class="mav-event-thought">{{ event.thought }}</div>
            <pre v-if="event.input" class="mav-event-code">{{ formatJson(event.input) }}</pre>
          </template>

          <!-- Tool result -->
          <template v-if="event.type === 'tool_result'">
            <div class="mav-event-header">
              <v-icon x-small color="#66BB6A">mdi-check-decagram</v-icon>
              <span class="mav-event-label">{{ event.tool_name }} result</span>
            </div>
            <details v-if="debugMode" class="mav-debug-details">
              <summary class="mav-debug-summary"
                >{{ event.tool_name }} — raw output ({{
                  (event.output || '').length
                }}
                chars)</summary
              >
              <pre class="mav-event-code">{{ formatToolOutput(event.output) }}</pre>
            </details>
            <pre v-else class="mav-event-code">{{ formatToolOutput(event.output) }}</pre>
          </template>

          <!-- Iteration summary (debug mode only) -->
          <template v-if="event.type === 'iteration_summary'">
            <div class="mav-event mav-event--debug-summary">
              <div class="mav-debug-iter-row">
                <span class="mav-debug-badge">{{ event.action }}</span>
                <span class="mav-debug-timing">{{ event.duration_ms }}ms</span>
                <span class="mav-debug-meta">
                  thought: {{ event.thought_length }} chars, output:
                  {{ event.output_length }} chars, history: {{ event.history_steps }} steps
                </span>
              </div>
            </div>
          </template>

          <!-- User choice required -->
          <template v-if="event.type === 'user_choice_required'">
            <div class="mav-event-header">
              <v-icon x-small color="#FFB74D">mdi-hand-back-right</v-icon>
              <span class="mav-event-label">Input Required</span>
              <v-chip x-small color="#FF9800" dark>{{ formatLevel(event.choice.level) }}</v-chip>
            </div>
            <div v-if="event.thought" class="mav-event-thought mb-2">{{ event.thought }}</div>
            <div class="mav-choice-message">{{ event.choice.message }}</div>

            <!-- Already responded -->
            <div v-if="event._userResponse" class="mav-choice-responded mt-2">
              <v-icon x-small color="#CE93D8" class="mr-1">mdi-check-circle</v-icon>
              <span>Chose: {{ event._userResponse.label }}</span>
            </div>

            <!-- Awaiting response -->
            <div v-else-if="!tab.resuming">
              <div class="mav-choice-header">
                <v-icon small color="#FF9800" class="mr-2">mdi-gesture-tap</v-icon>
                <span>Please select an option to continue:</span>
              </div>
              <div class="mav-choice-options mt-2">
                <!-- Select options (existing entities) - sorted with recommended first -->
                <div
                  v-for="(opt, oi) in getSortedOptions(event.choice.options, 'select')"
                  :key="'s-' + oi"
                  class="mav-choice-option"
                  :class="{ 'mav-choice-option--recommended': opt.recommended }"
                  @click="resumeWithChoice(tab, event, opt)"
                >
                  <div class="mav-choice-option-header">
                    <div class="mav-choice-option-name">
                      {{ opt.name || opt.parent_name }}
                      <span v-if="opt.id || opt.parent_id" class="mav-choice-option-id"
                        >#{{ opt.id || opt.parent_id }}</span
                      >
                    </div>
                    <span v-if="opt.recommended" class="mav-recommended-badge"
                      >Recommended hierarchy</span
                    >
                  </div>
                  <div v-if="opt.description" class="mav-choice-option-desc">
                    {{ opt.description }}
                  </div>
                  <!-- Show hierarchy details for hierarchy-level choices -->
                  <div
                    v-if="
                      event.choice.level === 'hierarchy' &&
                        (opt.parent_name || opt.brand_name || opt.channel_name)
                    "
                    class="mav-choice-hierarchy-details"
                  >
                    <span v-if="opt.parent_name" class="mav-hier-item"
                      >{{ opt.parent_name }}
                      <span v-if="opt.parent_id" class="mav-hier-id"
                        >#{{ opt.parent_id }}</span
                      ></span
                    >
                    <v-icon v-if="opt.parent_name && opt.brand_name" x-small class="mav-hier-sep"
                      >mdi-chevron-right</v-icon
                    >
                    <span v-if="opt.brand_name" class="mav-hier-item"
                      >{{ opt.brand_name }}
                      <span v-if="opt.brand_id" class="mav-hier-id">#{{ opt.brand_id }}</span></span
                    >
                    <v-icon v-if="opt.brand_name && opt.channel_name" x-small class="mav-hier-sep"
                      >mdi-chevron-right</v-icon
                    >
                    <span v-if="opt.channel_name" class="mav-hier-item"
                      >{{ opt.channel_name }}
                      <span v-if="opt.channel_id" class="mav-hier-id"
                        >#{{ opt.channel_id }}</span
                      ></span
                    >
                  </div>
                </div>
                <!-- Create options (new entities) -->
                <div
                  v-for="(opt, oi) in filterOptions(event.choice.options, 'create')"
                  :key="'c-' + oi"
                  class="mav-choice-create"
                  @click="resumeWithChoice(tab, event, opt)"
                >
                  <v-icon small color="#E1BEE7" class="mr-2">mdi-plus-circle</v-icon>
                  <span>{{ opt.label || 'Create new ' + event.choice.level }}</span>
                </div>
                <!-- Search / search_alternative options (re-search OWL) -->
                <div
                  v-for="(opt, oi) in filterOptions(event.choice.options, 'search')"
                  :key="'sr-' + oi"
                  class="mav-choice-search"
                  @click="resumeWithChoice(tab, event, opt)"
                >
                  <v-icon small color="#90CAF9" class="mr-2">mdi-magnify</v-icon>
                  <span>{{ opt.label || 'Search for alternative ' + event.choice.level }}</span>
                </div>
                <!-- Provide IDs options (manual ID entry) -->
                <div
                  v-for="(opt, oi) in filterOptions(event.choice.options, 'provide')"
                  :key="'p-' + oi"
                  class="mav-choice-provide"
                  @click="resumeWithChoice(tab, event, opt)"
                >
                  <v-icon small color="#A5D6A7" class="mr-2">mdi-pencil</v-icon>
                  <span>{{ opt.label || 'Provide existing ' + event.choice.level + ' IDs' }}</span>
                </div>
              </div>
            </div>

            <!-- Resuming spinner -->
            <div v-else class="mav-choice-resuming mt-2">
              <v-progress-circular indeterminate size="14" width="2" color="#7B1FA2" class="mr-2" />
              <span>Resuming agent...</span>
            </div>
          </template>

          <!-- OWL Approval Required -->
          <template v-if="event.type === 'owl_approval_required'">
            <div class="mav-event-header">
              <v-icon x-small color="#FFB74D">mdi-clock-alert</v-icon>
              <span class="mav-event-label">OWL Approval Pending</span>
              <v-chip x-small color="#FF9800" dark>{{
                formatHierarchyLevel(event.pending_owl && event.pending_owl.level)
              }}</v-chip>
            </div>
            <div v-if="event.thought" class="mav-event-thought mb-2">{{ event.thought }}</div>
            <div class="mav-choice-message">
              {{
                event.message ||
                  'Your hierarchy proposal is awaiting OWL approval before the agent can continue.'
              }}
            </div>

            <div class="mav-owl-pending mt-2">
              <v-icon small color="#FF9800" class="mr-2">mdi-information</v-icon>
              <div>
                <div v-if="event.pending_owl" class="mb-1">
                  <strong
                    >{{ formatHierarchyLevel(event.pending_owl.level) }}:
                    {{ event.pending_owl.name }}</strong
                  >
                  <span v-if="event.pending_owl.proposal_id" class="mav-answer-id ml-1"
                    >(Proposal #{{ event.pending_owl.proposal_id }})</span
                  >
                </div>
                <span>
                  The agent will automatically continue once OWL approval is granted. You can close
                  this window and resume later from the same domain or the
                  <router-link to="/cls/domain/sessions" class="mav-link">History tab</router-link>.
                </span>
              </div>
            </div>

            <div class="mav-owl-actions mt-3">
              <v-btn small text color="#90A4AE" @click="closeTab(tabs.indexOf(tab))">
                <v-icon small class="mr-1">mdi-close</v-icon> Close & Resume Later
              </v-btn>
            </div>
          </template>

          <!-- Resumed -->
          <template v-if="event.type === 'resumed'">
            <div class="mav-event-header">
              <v-icon x-small color="#CE93D8">mdi-play-circle</v-icon>
              <span class="mav-event-label">Resumed</span>
              <span class="mav-event-iter">Step {{ event.iteration }}</span>
            </div>
            <div v-if="event.thought" class="mav-event-body">{{ event.thought }}</div>
          </template>

          <!-- Hierarchy created -->
          <template v-if="event.type === 'hierarchy_created'">
            <div class="mav-event-header">
              <v-icon
                x-small
                :color="
                  event.hierarchy_result && event.hierarchy_result.approved ? '#4CAF50' : '#FF9800'
                "
              >
                {{
                  event.hierarchy_result && event.hierarchy_result.approved
                    ? 'mdi-check-circle'
                    : 'mdi-alert'
                }}
              </v-icon>
              <span class="mav-event-label"
                >{{
                  formatHierarchyLevel(event.hierarchy_result && event.hierarchy_result.level)
                }}
                Created</span
              >
              <span class="mav-event-iter">Step {{ event.iteration }}</span>
            </div>
            <div class="mav-hierarchy-created-body">
              <div class="mav-hierarchy-created-name">
                {{ event.hierarchy_result && event.hierarchy_result.name }}
                <span class="mav-answer-id"
                  >(ID: {{ event.hierarchy_result && event.hierarchy_result.entity_id }})</span
                >
              </div>
              <v-chip
                x-small
                :color="
                  event.hierarchy_result && event.hierarchy_result.approved ? '#4CAF50' : '#FF9800'
                "
                dark
                class="mt-1"
              >
                {{
                  event.hierarchy_result && event.hierarchy_result.approved
                    ? 'Approved'
                    : 'Pending Review'
                }}
              </v-chip>
              <div
                v-if="event.hierarchy_result && !event.hierarchy_result.approved"
                class="mav-hierarchy-warning mt-2"
              >
                <v-icon x-small color="#FF9800" class="mr-1">mdi-information</v-icon>
                Entity is not live yet — the final prediction may reference an ID that doesn't
                resolve until the proposal is reviewed.
              </div>
            </div>
          </template>

          <!-- Answer -->
          <template v-if="event.type === 'answer'">
            <div class="mav-event-header">
              <v-icon x-small color="#FFB74D">mdi-star</v-icon>
              <span class="mav-event-label">Prediction</span>
              <v-chip
                v-if="isDebugMode"
                x-small
                :color="confidenceColor(event.prediction)"
                dark
                class="mav-confidence-chip"
              >
                {{ Math.round((event.prediction.overall_confidence || 0) * 100) }}%
              </v-chip>
            </div>
            <div class="mav-answer-body">
              <div class="mav-answer-row">
                <span class="mav-answer-key">Parent</span
                ><span
                  >{{ event.prediction.parent_name || 'N/A' }}
                  <span class="mav-answer-id">({{ event.prediction.parent_id || '—' }})</span></span
                >
              </div>
              <div class="mav-answer-row">
                <span class="mav-answer-key">Brand</span
                ><span
                  >{{ event.prediction.brand_name || 'N/A' }}
                  <span class="mav-answer-id">({{ event.prediction.brand_id || '—' }})</span></span
                >
              </div>
              <div class="mav-answer-row">
                <span class="mav-answer-key">Channel</span
                ><span
                  >{{ event.prediction.channel_name || 'N/A' }}
                  <span class="mav-answer-id"
                    >({{ event.prediction.channel_id || '—' }})</span
                  ></span
                >
              </div>
              <div v-if="event.prediction.category_name" class="mav-answer-row">
                <span class="mav-answer-key">Category</span>
                <span
                  >{{ event.prediction.category_name
                  }}<template v-if="event.prediction.subcategory_name">
                    / {{ event.prediction.subcategory_name }}</template
                  ></span
                >
              </div>
              <div v-if="event.prediction.ad_supported" class="mav-answer-row">
                <span class="mav-answer-key">Ad</span
                ><span>{{ event.prediction.ad_supported }}</span>
              </div>
              <div
                v-if="event.prediction.reasoning_trace && event.prediction.reasoning_trace.length"
                class="mav-answer-trace"
              >
                <span class="mav-answer-key mav-answer-key--block">Reasoning</span>
                <div class="mav-answer-trace-body">
                  <span
                    v-for="(line, j) in event.prediction.reasoning_trace"
                    :key="j"
                    class="d-block"
                    >{{ line }}</span
                  >
                </div>
              </div>
              <div class="mav-answer-actions">
                <v-btn
                  v-if="!needsHierarchyCreation(event.prediction)"
                  small
                  class="mav-apply-btn"
                  dark
                  @click="applyPrediction(tab)"
                >
                  <v-icon small class="mr-1">mdi-check</v-icon> Apply Tree ID
                </v-btn>
                <v-btn v-else small color="#FF9800" dark @click="createHierarchy(tab)">
                  <v-icon small class="mr-1">mdi-plus-circle</v-icon> Create Hierarchy
                </v-btn>
                <v-btn
                  v-if="!needsHierarchyCreation(event.prediction)"
                  small
                  class="mav-proposal-btn ml-2"
                  dark
                  :loading="tab.proposalLoading"
                  @click="createProposal(tab)"
                >
                  <v-icon small class="mr-1">mdi-content-save</v-icon> Create Proposal
                </v-btn>
              </div>
            </div>
          </template>

          <!-- Error -->
          <template v-if="event.type === 'error'">
            <div class="mav-event-header">
              <v-icon x-small color="#EF5350">mdi-alert-circle</v-icon>
              <span class="mav-event-label" style="color: #EF5350">Error</span>
              <span class="mav-event-iter">Step {{ event.iteration }}</span>
            </div>
            <div class="mav-event-body" style="color: #EF9A9A">{{ event.output }}</div>
            <v-btn x-small text color="#CE93D8" class="mt-1" @click="retryTab(tab)">
              <v-icon x-small class="mr-1">mdi-refresh</v-icon> Retry
            </v-btn>
          </template>
        </div>

        <!-- Streaming thought -->
        <div v-if="tab.streamingThought" class="mav-event mav-event--thought">
          <div class="mav-event-header">
            <v-icon x-small color="#CE93D8">mdi-head-lightbulb</v-icon>
            <span class="mav-event-label">Thinking</span>
            <span class="mav-event-iter">Step {{ tab.streamingThought.iteration }}</span>
            <v-progress-circular indeterminate size="10" width="1" color="#CE93D8" class="ml-2" />
          </div>
          <div class="mav-event-body">
            {{ tab.streamingThought.text }}<span class="mav-cursor">▊</span>
          </div>
        </div>

        <!-- Session expired -->
        <div v-if="tab.expired" class="mav-event mav-event--error mt-2">
          <v-icon x-small color="#EF5350" class="mr-1">mdi-timer-off</v-icon>
          <span style="color: #EF9A9A">Session expired. Please restart classification.</span>
          <v-btn x-small text color="#CE93D8" class="ml-2" @click="retryTab(tab)">Restart</v-btn>
        </div>

        <!-- Connection error -->
        <div v-if="tab.error && !tab.events.some(e => e.type === 'error')" class="mt-2 px-2">
          <v-alert dense type="error" text class="mb-0 text-caption">
            {{ tab.error }}
            <v-btn x-small text color="error" class="ml-2" @click="retryTab(tab)">Retry</v-btn>
          </v-alert>
        </div>
      </div>
    </div>

    <div v-if="!minimized" class="mav-resize-handle" @mousedown.stop="startResize" />
  </div>
</template>

<script>
import { getToken } from '@nielsen-media/ui-toolkit-vue-api';
import { MavStreamBus } from '@/helpers/mavStreamBus';
import { getConfigMap } from '@/helpers/filters';
import HTTP from '@/api/ant';

const MAV_BASE_URL = process.env.VUE_APP_MAV_API_URL;
const MAV_V2_URL = MAV_BASE_URL.replace(/\/v1\/?$/, '/v2');
const MAV_API_KEY = process.env.VUE_APP_MAV_API_KEY;
const SESSION_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

let tabIdCounter = 0;

export default {
  name: 'MavStreamWindow',
  data() {
    return {
      tabs: [],
      activeTab: 0,
      minimized: false,
      debugMode: false,
      x: 100,
      y: 100,
      width: 416,
      height: 368,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOffsetX: 0,
      dragOffsetY: 0,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartW: 0,
      resizeStartH: 0,
      tabDragIndex: null,
      dragOverIndex: null,
    };
  },
  computed: {
    isDebugMode() {
      const config = getConfigMap.call(this);
      return config['mavFeature_owl_ui']?.enabled?.debug === true;
    },
    windowStyle() {
      return {
        left: this.x + 'px',
        top: this.y + 'px',
        width: this.width + 'px',
        height: this.minimized ? 'auto' : this.height + 'px',
      };
    },
    predictUrl() {
      return this.debugMode ? `${MAV_V2_URL}/predict/domain/debug` : `${MAV_V2_URL}/predict/domain`;
    },
  },
  created() {
    MavStreamBus.$on('mav-stream-predict', this.openTab);
    MavStreamBus.$on('mav-stream-resume', this.resumeSessionTab);
  },
  beforeDestroy() {
    MavStreamBus.$off('mav-stream-predict', this.openTab);
    MavStreamBus.$off('mav-stream-resume', this.resumeSessionTab);
    this.tabs.forEach(tab => {
      this.abortStream(tab);
      clearTimeout(tab.sessionTimer);
    });
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.stopResize);
  },
  methods: {
    // --- Tab reorder ---
    onTabDragStart(index, e) {
      this.tabDragIndex = index;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    },
    onTabDragOver(index) {
      if (this.tabDragIndex !== null && this.tabDragIndex !== index) this.dragOverIndex = index;
    },
    onTabDragLeave() {
      this.dragOverIndex = null;
    },
    onTabDrop(targetIndex) {
      this.dragOverIndex = null;
      if (this.tabDragIndex === null || this.tabDragIndex === targetIndex) return;
      const tab = this.tabs.splice(this.tabDragIndex, 1)[0];
      this.tabs.splice(targetIndex, 0, tab);
      this.activeTab = targetIndex;
      this.tabDragIndex = null;
    },
    onTabDragEnd() {
      this.tabDragIndex = null;
      this.dragOverIndex = null;
    },

    createTab(domain, countries, itemId, callback) {
      return {
        id: ++tabIdCounter,
        domain,
        countries,
        itemId,
        callback,
        events: [],
        streaming: true,
        paused: false,
        resuming: false,
        error: null,
        expired: false,
        prediction: null,
        abortController: null,
        streamingThought: null,
        sessionId: null,
        sessionTimer: null,
        isDebug: this.debugMode,
        proposalLoading: false,
        // Track entity IDs created during the hierarchy_created flow
        // so they can be passed back as parent_id / brand_id on subsequent resumes.
        createdParentId: null,
        createdBrandId: null,
      };
    },

    openTab({ domain, countries, itemId, callback }) {
      const tab = this.createTab(domain, countries, itemId, callback);
      this.tabs.push(tab);
      this.activeTab = this.tabs.length - 1;
      this.$nextTick(() =>
        this.connectStream(tab, this.predictUrl, {
          domain: tab.domain,
          countries: tab.countries || 'us',
        }),
      );
    },

    async resumeSessionTab({ sessionId, domain }) {
      // Check if tab already exists for this session
      const existingIndex = this.tabs.findIndex(t => t.sessionId === sessionId);
      if (existingIndex !== -1) {
        this.activeTab = existingIndex;
        return;
      }

      const tab = this.createTab(domain || 'Resuming...', 'us', null, null);
      tab.sessionId = sessionId;
      tab.resuming = true;
      tab.streaming = true;
      this.tabs.push(tab);
      this.activeTab = this.tabs.length - 1;

      const resumeUrl = tab.isDebug
        ? `${MAV_V2_URL}/predict/domain/resume/debug`
        : `${MAV_V2_URL}/predict/domain/resume`;

      this.$nextTick(async () => {
        try {
          await this.connectStream(tab, resumeUrl, { session_id: sessionId });
        } catch (error) {
          tab.error = error.message || 'Failed to resume session';
          tab.streaming = false;
        } finally {
          tab.resuming = false;
        }
      });
    },

    async resumeWithChoice(tab, choiceEvent, option) {
      if (!tab.sessionId) return;

      // Build a human-readable label for the responded state.
      // Each action type has a different preferred label source.
      let displayLabel;
      switch (option.action) {
        case 'create':
          displayLabel = option.label || option.name || `Create new ${choiceEvent.choice.level}`;
          break;
        case 'search':
        case 'search_alternative':
          displayLabel = option.label || `Search for alternative ${choiceEvent.choice.level}`;
          break;
        case 'provide_ids':
        case 'provide_id':
          displayLabel = option.label || `Provide existing ${choiceEvent.choice.level} IDs`;
          break;
        case 'select_alternative':
          displayLabel =
            option.name || option.label || `Select alternative ${choiceEvent.choice.level}`;
          break;
        default:
          // 'select' and any future action types
          displayLabel = option.name || option.parent_name || option.label;
      }
      this.$set(choiceEvent, '_userResponse', { action: option.action, label: displayLabel });
      tab.paused = false;
      tab.resuming = true;
      tab.streaming = true;
      clearTimeout(tab.sessionTimer);

      this.$nextTick(() => this.scrollToBottom(tab));

      // Build the resume payload - send all fields from the option
      const payload = {
        session_id: tab.sessionId,
        action: option.action,
        level: choiceEvent.choice.level,
      };

      // Include all IDs and names from the option
      // This is especially important for hierarchy-level choices where
      // parent, brand, and channel IDs may all be present
      if (option.id != null) payload.id = option.id;
      if (option.name) payload.name = option.name;

      // Parent fields
      if (option.parent_id != null) payload.parent_id = option.parent_id;
      if (option.parent_name) payload.parent_name = option.parent_name;

      // Brand fields
      if (option.brand_id != null) payload.brand_id = option.brand_id;
      if (option.brand_name) payload.brand_name = option.brand_name;

      // Channel fields
      if (option.channel_id != null) payload.channel_id = option.channel_id;
      if (option.channel_name) payload.channel_name = option.channel_name;

      // Additional context from choice
      const ctx = (choiceEvent.choice && choiceEvent.choice.context) || {};

      // For create actions, add additional context fields
      if (option.action === 'create') {
        // Fallback to tab's created IDs if not in option
        if (!payload.parent_id && tab.createdParentId) {
          payload.parent_id = tab.createdParentId;
        } else if (!payload.parent_id && ctx.parent_id) {
          payload.parent_id = ctx.parent_id;
        }

        if (!payload.brand_id && tab.createdBrandId) {
          payload.brand_id = tab.createdBrandId;
        } else if (!payload.brand_id && ctx.brand_id) {
          payload.brand_id = ctx.brand_id;
        }

        // ad_supported — optional, applies to brand and channel creation.
        if (option.ad_supported != null) {
          payload.ad_supported = option.ad_supported;
        } else if (ctx.ad_supported != null) {
          payload.ad_supported = ctx.ad_supported;
        }

        // country_codes — optional, ISO codes for parent creation.
        if (Array.isArray(option.country_codes) && option.country_codes.length) {
          payload.country_codes = option.country_codes;
        } else if (Array.isArray(ctx.country_codes) && ctx.country_codes.length) {
          payload.country_codes = ctx.country_codes;
        }
      }

      await this.connectStream(
        tab,
        tab.isDebug
          ? `${MAV_V2_URL}/predict/domain/resume/debug`
          : `${MAV_V2_URL}/predict/domain/resume`,
        payload,
      );

      tab.resuming = false;
    },

    retryTab(tab) {
      const { domain, countries, itemId, callback } = tab;
      const index = this.tabs.indexOf(tab);
      this.abortStream(tab);
      clearTimeout(tab.sessionTimer);

      const newTab = this.createTab(domain, countries, itemId, callback);
      if (index !== -1) {
        this.tabs.splice(index, 1, newTab);
        this.activeTab = index;
      } else {
        this.tabs.push(newTab);
        this.activeTab = this.tabs.length - 1;
      }
      this.$nextTick(() =>
        this.connectStream(newTab, this.predictUrl, {
          domain: newTab.domain,
          countries: newTab.countries || 'us',
        }),
      );
    },

    async connectStream(tab, url, body) {
      try {
        const token = await getToken();
        const controller = new AbortController();
        tab.abortController = controller;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-API-Key': MAV_API_KEY,
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          tab.error = `API error: ${response.status} ${response.statusText}`;
          tab.streaming = false;
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let active = true;
        while (active) {
          const { done, value } = await reader.read();
          if (done) {
            active = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // SSE events are delimited by double newlines
          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const chunk = buffer.slice(0, boundary).trim();
            buffer = buffer.slice(boundary + 2);

            if (chunk.startsWith('data: ')) {
              try {
                const event = JSON.parse(chunk.slice(6));
                this.handleEvent(tab, event);
              } catch (e) {
                /* skip malformed */
              }
            }

            boundary = buffer.indexOf('\n\n');
          }
        }

        // Flush remaining buffer
        const remaining = buffer.trim();
        if (remaining.startsWith('data: ')) {
          try {
            this.handleEvent(tab, JSON.parse(remaining.slice(6)));
          } catch (e) {
            /* skip */
          }
        }

        if (!tab.paused) tab.streaming = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          tab.error = error.message || 'Stream connection failed';
        }
        tab.streaming = false;
      }
    },

    handleEvent(tab, event) {
      if (event.type === 'thought_token') {
        if (!tab.streamingThought || tab.streamingThought.iteration !== event.iteration) {
          this.$set(tab, 'streamingThought', { iteration: event.iteration, text: '' });
        }
        tab.streamingThought.text += event.token;
        this.$nextTick(() => this.scrollToBottom(tab));
        return;
      }

      if (event.type === 'thought_done') {
        if (tab.streamingThought && tab.streamingThought.iteration === event.iteration) {
          tab.events.push({
            type: 'thought',
            iteration: event.iteration,
            thought: tab.streamingThought.text,
          });
          this.$set(tab, 'streamingThought', null);
        }
        this.$nextTick(() => this.scrollToBottom(tab));
        return;
      }

      if (event.type === 'thought') {
        if (tab.events.some(e => e.type === 'thought' && e.iteration === event.iteration)) return;
        this.$set(tab, 'streamingThought', null);
      }

      if (event.type === 'user_choice_required') {
        tab.sessionId = event.session_id;
        tab.paused = true;
        tab.streaming = false;
        // Start session expiry timer
        clearTimeout(tab.sessionTimer);
        tab.sessionTimer = setTimeout(() => {
          if (tab.paused) {
            tab.expired = true;
            tab.paused = false;
          }
        }, SESSION_TIMEOUT_MS);
      }

      if (event.type === 'owl_approval_required') {
        tab.sessionId = event.session_id;
        tab.paused = true;
        tab.streaming = false;
        // Start session expiry timer
        clearTimeout(tab.sessionTimer);
        tab.sessionTimer = setTimeout(() => {
          if (tab.paused) {
            tab.expired = true;
            tab.paused = false;
          }
        }, SESSION_TIMEOUT_MS);
      }

      if (event.type === 'resumed') {
        tab.paused = false;
        tab.resuming = false;
        tab.streaming = true;
      }

      // Stash entity IDs from hierarchy_created so subsequent resume
      // calls can pass them as parent_id / brand_id.
      if (event.type === 'hierarchy_created' && event.hierarchy_result) {
        const hr = event.hierarchy_result;
        if (hr.level === 'parent') {
          tab.createdParentId = hr.entity_id;
        } else if (hr.level === 'brand') {
          tab.createdBrandId = hr.entity_id;
        }
      }

      if (event.type === 'answer' && event.prediction) {
        tab.prediction = event.prediction;
        tab.streaming = false;
      }

      if (event.type === 'error') {
        tab.streaming = false;
      }

      // iteration_summary is just pushed to events (rendered in template)
      tab.events.push(event);
      this.$nextTick(() => this.scrollToBottom(tab));
    },

    abortStream(tab) {
      if (tab.abortController) {
        tab.abortController.abort();
        tab.abortController = null;
      }
    },

    scrollToBottom(tab) {
      const refs = this.$refs['stream-' + tab.id];
      const el = Array.isArray(refs) ? refs[0] : refs;
      if (el) el.scrollTop = el.scrollHeight;
    },

    applyPrediction(tab) {
      if (tab.prediction && tab.callback) tab.callback(tab.prediction);
      const index = this.tabs.indexOf(tab);
      if (index !== -1) this.closeTab(index);
    },

    async createProposal(tab) {
      if (!tab.prediction || !tab.itemId) return;

      const prediction = tab.prediction;
      // Determine tree_id using the same hierarchy fallback as ClassifyRow
      let tree_id = null;
      if (Number.isFinite(prediction.channel_id) && prediction.channel_id > 0) {
        tree_id = prediction.channel_id;
      } else if (Number.isFinite(prediction.brand_id) && prediction.brand_id > 0) {
        tree_id = prediction.brand_id;
      } else if (Number.isFinite(prediction.parent_id) && prediction.parent_id > 0) {
        tree_id = prediction.parent_id;
      }

      if (!tree_id) {
        this.$flash && this.$flash('error', 'No valid tree ID in prediction');
        return;
      }

      tab.proposalLoading = true;
      try {
        const payload = {
          rules: [
            {
              pattern: tab.domain,
              tree_id,
              localization: '*',
              effective_start_date: null,
              created_via_mav: true,
            },
          ],
        };
        await HTTP.post(`/domain/discoveries/${tab.itemId}/multiplerules`, payload, {
          niSkipFlash: true,
        });
        this.$flash && this.$flash('success', `Proposal created for ${tab.domain}`);
        const index = this.tabs.indexOf(tab);
        if (index !== -1) this.closeTab(index);
      } catch (err) {
        if (err.response?.status === 400 && err.response?.data?.errors) {
          const key = Object.keys(err.response.data.errors)[0];
          this.$flash && this.$flash('error', `${key} ${err.response.data.errors[key]}`);
        } else {
          this.$flash && this.$flash('error', 'Failed to create proposal');
        }
      } finally {
        tab.proposalLoading = false;
      }
    },

    needsHierarchyCreation(prediction) {
      // Placeholder IDs from the agent's "create" flow are small sequential numbers (1, 2, 3)
      // Real OWL IDs are large (thousands+). Treat IDs < 100 as placeholders needing creation.
      const isPlaceholder = id => !id || id < 100;
      return (
        isPlaceholder(prediction.parent_id) ||
        isPlaceholder(prediction.brand_id) ||
        isPlaceholder(prediction.channel_id)
      );
    },

    createHierarchy(tab) {
      if (tab.prediction && tab.callback) {
        tab.callback({
          ...tab.prediction,
          _needsCreation: true,
        });
      }
      const index = this.tabs.indexOf(tab);
      if (index !== -1) this.closeTab(index);
    },

    closeTab(index) {
      const tab = this.tabs[index];
      this.abortStream(tab);
      clearTimeout(tab.sessionTimer);
      this.tabs.splice(index, 1);
      if (this.activeTab >= this.tabs.length) this.activeTab = Math.max(0, this.tabs.length - 1);
    },

    closeAll() {
      this.tabs.forEach(tab => {
        this.abortStream(tab);
        clearTimeout(tab.sessionTimer);
      });
      this.tabs = [];
      this.activeTab = 0;
    },

    isDebugEnabled() {
      const config = this.getConfigMap();
      if (config['mavFeature_owl_ui']?.enabled?.v1)
        return config['mavFeature_owl_ui']?.enabled?.debug;
    },

    // --- Drag ---
    startDrag(e) {
      if (e.target.closest('.v-btn')) return;
      this.dragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragOffsetX = this.x;
      this.dragOffsetY = this.y;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      e.preventDefault();
    },
    onDrag(e) {
      if (!this.dragging) return;
      this.x = Math.min(
        Math.max(0, this.dragOffsetX + (e.clientX - this.dragStartX)),
        window.innerWidth - this.width,
      );
      this.y = Math.min(
        Math.max(0, this.dragOffsetY + (e.clientY - this.dragStartY)),
        window.innerHeight - 40,
      );
    },
    stopDrag() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    // --- Resize ---
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartW = this.width;
      this.resizeStartH = this.height;
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      e.preventDefault();
    },
    onResize(e) {
      if (!this.resizing) return;
      this.width = Math.max(320, this.resizeStartW + (e.clientX - this.resizeStartX));
      this.height = Math.max(240, this.resizeStartH + (e.clientY - this.resizeStartY));
    },
    stopResize() {
      this.resizing = false;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
    },

    // --- Helpers ---
    formatJson(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(obj);
      }
    },
    formatToolOutput(output) {
      try {
        return JSON.stringify(JSON.parse(output), null, 2);
      } catch {
        return output;
      }
    },
    formatLevel(level) {
      return (
        { parent: 'Parent Selection', brand: 'Brand Selection', channel: 'Channel Selection' }[
          level
        ] || level
      );
    },
    formatHierarchyLevel(level) {
      return { parent: 'Parent', brand: 'Brand', channel: 'Channel' }[level] || level;
    },
    confidenceColor(prediction) {
      const c = prediction.overall_confidence || 0;
      if (c >= 0.8) return 'success';
      if (c >= 0.6) return 'warning';
      return 'error';
    },

    /**
     * Filter user-choice options by action category.
     * Groups related action types so the template can render them with
     * the appropriate visual treatment:
     *   'select'  → 'select', 'select_alternative'
     *   'create'  → 'create'
     *   'search'  → 'search', 'search_alternative'
     *   'provide' → 'provide_ids', 'provide_id'
     */
    filterOptions(options, category) {
      if (!options) return [];
      const groups = {
        select: ['select', 'select_alternative'],
        create: ['create'],
        search: ['search', 'search_alternative'],
        provide: ['provide_ids', 'provide_id'],
      };
      const actions = groups[category] || [category];
      return options.filter(o => actions.includes(o.action));
    },

    /**
     * Sort options with recommended first, then by original order.
     * Missing `recommended` field is treated as false (not recommended).
     */
    getSortedOptions(options, category) {
      const filtered = this.filterOptions(options, category);
      return [...filtered].sort((a, b) => {
        const aRecommended = a.recommended === true;
        const bRecommended = b.recommended === true;
        return Number(bRecommended) - Number(aRecommended);
      });
    },

    getConfigMap,
  },
};
</script>

<style scoped lang="scss">
// ─── Window Shell ───
.mav-stream-window {
  position: fixed;
  z-index: 1000;
  background: #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 320px;
}

// ─── Title Bar ───
.mav-title-bar {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #263238;
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  gap: 8px;
  &:active {
    cursor: grabbing;
  }
}
.mav-title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}
.mav-title-text {
  color: #b0bec5;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 500;
}

// ─── Tab Bar ───
.mav-tab-bar {
  display: flex;
  background: #212121;
  overflow-x: auto;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  &::-webkit-scrollbar {
    height: 0;
  }
}
.mav-tab-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: #78909c;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
  flex-shrink: 0;
  gap: 6px;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover {
    color: #b0bec5;
    background: rgba(255, 255, 255, 0.03);
  }
  &--active {
    color: #eceff1;
    font-weight: 500;
    border-bottom-color: #7b1fa2;
    background: rgba(123, 31, 162, 0.08);
  }
  &--drag-over {
    border-left: 2px solid #7b1fa2;
  }
  &--paused {
    border-bottom-color: #ff9800;
  }
}
.mav-tab-drag-grip {
  color: #546e7a !important;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
}
.mav-tab-close {
  opacity: 0.3;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
}

// ─── Content Area ───
.mav-content {
  flex: 1;
  overflow: hidden;
  position: relative;
}
.mav-stream-output {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  padding: 12px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #424242;
    border-radius: 3px;
    &:hover {
      background: #616161;
    }
  }
}

// ─── Event Cards ───
.mav-event {
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #272727;
  border-left: 3px solid transparent;
}
.mav-event--thought {
  border-left-color: #7b1fa2;
}
.mav-event--tool_call {
  border-left-color: #42a5f5;
}
.mav-event--tool_result {
  border-left-color: #4caf50;
}
.mav-event--answer {
  border-left-color: #ff9800;
}
.mav-event--user_choice_required {
  border-left-color: #ff9800;
}
.mav-event--resumed {
  border-left-color: #7b1fa2;
}
.mav-event--hierarchy_created {
  border-left-color: #4caf50;
}
.mav-event--error {
  background: rgba(244, 67, 54, 0.06);
  border-left-color: #f44336;
}

.mav-event-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 8px;
}
.mav-event-label {
  color: #cfd8dc;
  font-family: Roboto, sans-serif;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.mav-event-iter {
  color: #546e7a;
  font-family: Roboto, sans-serif;
  font-size: 11px;
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.mav-event-body {
  color: #cfd8dc;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.mav-event-thought {
  color: #90a4ae;
  font-family: Roboto, sans-serif;
  font-style: italic;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
.mav-cursor {
  animation: mav-blink 0.8s step-end infinite;
  color: #ce93d8;
  font-size: 12px;
}
@keyframes mav-blink {
  50% {
    opacity: 0;
  }
}

.mav-event-code {
  color: #90a4ae;
  background: #1a1a1a;
  padding: 8px 10px;
  border-radius: 4px;
  font-family: 'Roboto Mono', 'SF Mono', monospace;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow-y: auto;
  margin: 4px 0 0;
  line-height: 1.5;
}

// ─── Answer / Prediction ───
.mav-answer-body {
  color: #eceff1;
  font-family: Roboto, sans-serif;
  padding: 6px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mav-answer-row {
  display: flex;
  gap: 12px;
  font-size: 12px;
  line-height: 1.6;
  align-items: baseline;
}
.mav-answer-key {
  color: #78909c;
  min-width: 64px;
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  &--block {
    display: block;
    margin-bottom: 4px;
    min-width: 0;
  }
}
.mav-answer-id {
  color: #546e7a;
  font-size: 11px;
  font-family: 'Roboto Mono', monospace;
}
.mav-answer-trace {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.mav-answer-trace-body {
  color: #90a4ae;
  font-size: 12px;
  line-height: 1.55;
  padding-left: 0;
}
.mav-answer-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.mav-confidence-chip {
  font-weight: 600 !important;
  letter-spacing: 0.02em;
}
.mav-apply-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3) !important;
}
.mav-proposal-btn {
  background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%) !important;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3) !important;
}

// ─── Choice UI ───
.mav-choice-message {
  color: #eceff1;
  font-family: Roboto, sans-serif;
  font-size: 13.5px;
  line-height: 1.6;
  font-weight: 500;
  padding: 8px 12px;
  background: rgba(255, 152, 0, 0.08);
  border-radius: 4px;
  border-left: 3px solid #ff9800;
}
.mav-choice-header {
  display: flex;
  align-items: center;
  color: #ffb74d;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 12px;
  background: rgba(255, 152, 0, 0.12);
  border-radius: 4px;
  border-left: 3px solid #ff9800;
  margin-top: 12px;
}
.mav-choice-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mav-choice-option {
  padding: 12px 14px;
  border-radius: 6px;
  cursor: pointer;
  background: #303030;
  border: 2px solid rgba(123, 31, 162, 0.3);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  &:hover {
    background: #3a3a3a;
    border-color: #7b1fa2;
    box-shadow: 0 2px 8px rgba(123, 31, 162, 0.3);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
  &--recommended {
    border-color: rgba(11, 110, 79, 0.5);
    background: rgba(11, 110, 79, 0.08);
    &:hover {
      border-color: #0b6e4f;
      background: rgba(11, 110, 79, 0.12);
      box-shadow: 0 2px 8px rgba(11, 110, 79, 0.3);
    }
  }
}
.mav-choice-option-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.mav-choice-option-name {
  color: #eceff1;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
}
.mav-choice-option-id {
  color: #90a4ae;
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
  font-size: 11px;
  margin-left: 4px;
}
.mav-choice-option-desc {
  color: #90a4ae;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
}
.mav-recommended-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0b6e4f;
  background: #e6f6ef;
  border-radius: 999px;
  flex-shrink: 0;
}
.mav-choice-hierarchy-details {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  font-size: 11px;
}
.mav-hier-item {
  color: #b0bec5;
  font-family: Roboto, sans-serif;
}
.mav-hier-id {
  color: #546e7a;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  margin-left: 2px;
}
.mav-hier-sep {
  color: #546e7a !important;
}
.mav-choice-create {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  border-radius: 6px;
  cursor: pointer;
  background: linear-gradient(135deg, rgba(123, 31, 162, 0.15) 0%, rgba(123, 31, 162, 0.08) 100%);
  border: 2px solid rgba(123, 31, 162, 0.5);
  color: #e1bee7;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(123, 31, 162, 0.2);
  &:hover {
    background: linear-gradient(135deg, rgba(123, 31, 162, 0.25) 0%, rgba(123, 31, 162, 0.15) 100%);
    border-color: #7b1fa2;
    box-shadow: 0 4px 12px rgba(123, 31, 162, 0.4);
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
}
.mav-choice-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 14px;
  border-radius: 6px;
  cursor: pointer;
  background: rgba(66, 165, 246, 0.08);
  border: 2px solid rgba(66, 165, 246, 0.4);
  color: #90caf9;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  &:hover {
    background: rgba(66, 165, 246, 0.15);
    border-color: #42a5f5;
    box-shadow: 0 2px 8px rgba(66, 165, 246, 0.3);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
}
.mav-choice-provide {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 14px;
  border-radius: 6px;
  cursor: pointer;
  background: rgba(102, 187, 106, 0.08);
  border: 2px solid rgba(102, 187, 106, 0.4);
  color: #a5d6a7;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  &:hover {
    background: rgba(102, 187, 106, 0.15);
    border-color: #66bb6a;
    box-shadow: 0 2px 8px rgba(102, 187, 106, 0.3);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
}
.mav-choice-responded {
  display: flex;
  align-items: center;
  color: #ce93d8;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 10px;
  background: rgba(123, 31, 162, 0.08);
  border-radius: 4px;
}

// ─── Hierarchy Created ───
.mav-hierarchy-created-body {
  padding: 4px 0 0;
}
.mav-hierarchy-created-name {
  color: #eceff1;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 500;
}
.mav-hierarchy-warning {
  display: flex;
  align-items: flex-start;
  color: #ffb74d;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 10px;
  background: rgba(255, 152, 0, 0.06);
  border-radius: 4px;
  border: 1px solid rgba(255, 152, 0, 0.15);
}
.mav-choice-resuming {
  display: flex;
  align-items: center;
  color: #ce93d8;
  font-family: Roboto, sans-serif;
  font-size: 12px;
}

// ─── OWL Approval Pending ───
.mav-owl-pending {
  display: flex;
  align-items: flex-start;
  color: #ffb74d;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
  background: rgba(255, 152, 0, 0.06);
  border-radius: 4px;
  border: 1px solid rgba(255, 152, 0, 0.15);
  gap: 8px;
}

.mav-owl-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.mav-link {
  color: #90caf9;
  text-decoration: none;
  &:hover {
    color: #64b5f6;
    text-decoration: underline;
  }
}

.mav-event--owl_approval_required {
  border-left-color: #ff9800;
}

// ─── Resize Handle ───
.mav-resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 50%, rgba(120, 144, 156, 0.3) 50%);
  border-radius: 0 0 8px 0;
}

// ─── Debug Mode ───
.mav-debug-banner {
  display: flex;
  align-items: center;
  padding: 5px 14px;
  background: rgba(255, 193, 7, 0.08);
  border-bottom: 1px solid rgba(255, 193, 7, 0.15);
  color: #ffc107;
  font-family: Roboto, sans-serif;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  gap: 6px;
}
.mav-event--debug-summary,
.mav-event--iteration_summary {
  background: rgba(255, 193, 7, 0.04);
  border-left-color: #795548;
  padding: 6px 10px;
}
.mav-debug-iter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
}
.mav-debug-badge {
  background: rgba(255, 193, 7, 0.12);
  color: #ffc107;
  padding: 2px 8px;
  border-radius: 3px;
  font-weight: 600;
  font-size: 10px;
}
.mav-debug-timing {
  color: #4caf50;
  font-weight: 600;
}
.mav-debug-meta {
  color: #546e7a;
}
.mav-debug-details {
  margin: 0;
}
.mav-debug-summary {
  cursor: pointer;
  color: #ce93d8;
  font-size: 11px;
  padding: 2px 0;
  &:hover {
    color: #e1bee7;
  }
  &::marker {
    color: #546e7a;
  }
}
</style>
